<script setup lang="ts">
  import { RotateCw, ExternalLink, Smartphone, Monitor, Settings } from "lucide-vue-next";
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";

  interface CountryOption {
    value: string;
    label: string;
  }

  interface ContactOption {
    value: string;
    label: string;
  }

  defineProps<{
    previewMode: "phone" | "desktop";
    settingsOpen: boolean;
    pParamDraft: string;
    extraParamsDraft: string;
    countryDraft: string;
    contactDraft: string;
    isMultiCountry: boolean;
    isBrevoTest: boolean;
    targetCountryOptions: CountryOption[];
    testContactOptions: ContactOption[];
  }>();

  const emit = defineEmits<{
    reload: [];
    openSettings: [];
    applySettings: [];
    openExternal: [];
    "update:settingsOpen": [value: boolean];
    "update:previewMode": [value: "phone" | "desktop"];
    "update:pParamDraft": [value: string];
    "update:extraParamsDraft": [value: string];
    "update:countryDraft": [value: string];
    "update:contactDraft": [value: string];
  }>();
</script>

<template>
  <div class="flex flex-col items-center gap-2">
    <!-- Reload button -->
    <Tooltip
      content="Reload"
      side="right">
      <Button
        id="reload-chat"
        variant="outline"
        size="icon"
        class="h-9 w-9 shadow-sm"
        @click="emit('reload')">
        <RotateCw class="h-4 w-4" />
      </Button>
    </Tooltip>

    <!-- Settings popover -->
    <Popover
      :open="settingsOpen"
      @update:open="emit('update:settingsOpen', $event)">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          size="icon"
          class="h-9 w-9 shadow-sm"
          @click="emit('openSettings')">
          <Settings class="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        class="w-56"
        @open-auto-focus.prevent>
        <div class="space-y-4">
          <!-- P parameter -->
          <div class="space-y-2">
            <Label>Page (URL "p" parameter)</Label>
            <Input
              :model-value="pParamDraft"
              placeholder="episto"
              @update:model-value="emit('update:pParamDraft', $event)" />
          </div>

          <!-- Extra URL parameters -->
          <div class="space-y-2">
            <Label>Extra URL parameters</Label>
            <Input
              :model-value="extraParamsDraft"
              placeholder="key1=val1&key2=..."
              @update:model-value="emit('update:extraParamsDraft', $event)" />
          </div>

          <!-- Country select -->
          <div
            v-if="isMultiCountry"
            class="space-y-2">
            <Label>Country</Label>
            <Select
              :model-value="countryDraft"
              :options="targetCountryOptions"
              placeholder="Select country..."
              @update:model-value="emit('update:countryDraft', $event)" />
          </div>

          <!-- Contact select -->
          <div
            v-if="isBrevoTest"
            class="space-y-2">
            <Label>Test contact</Label>
            <Select
              :model-value="contactDraft"
              :options="testContactOptions"
              placeholder="Select contact..."
              @update:model-value="emit('update:contactDraft', $event)" />
          </div>

          <!-- OK button -->
          <Button
            class="w-full"
            @click="emit('applySettings')">
            OK
          </Button>
        </div>
      </PopoverContent>
    </Popover>

    <!-- Open in new tab button -->
    <Tooltip
      content="Open in new tab"
      side="right">
      <Button
        id="test-link"
        variant="outline"
        size="icon"
        class="h-9 w-9 shadow-sm"
        @click="emit('openExternal')">
        <ExternalLink class="h-4 w-4" />
      </Button>
    </Tooltip>

    <!-- Preview mode toggle -->
    <div class="flex flex-col bg-background rounded-md p-1 shadow-sm border">
      <Tooltip
        content="Phone"
        side="right">
        <button
          :class="[
            'h-8 w-8 flex items-center justify-center rounded transition-colors',
            previewMode === 'phone' ? 'bg-muted' : 'hover:bg-muted/50',
          ]"
          @click="emit('update:previewMode', 'phone')">
          <Smartphone class="h-4 w-4" />
        </button>
      </Tooltip>
      <Tooltip
        content="Desktop"
        side="right">
        <button
          :class="[
            'h-8 w-8 flex items-center justify-center rounded transition-colors',
            previewMode === 'desktop' ? 'bg-muted' : 'hover:bg-muted/50',
          ]"
          @click="emit('update:previewMode', 'desktop')">
          <Monitor class="h-4 w-4" />
        </button>
      </Tooltip>
    </div>
  </div>
</template>
